import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  Menu,
  Modal,
  Row,
  Select,
  Table,
  Tag,
  message,
} from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  DownOutlined,
  MobileOutlined,
  HeartOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import EditableFormRow from './EditableFormRow';
import EditableCell from './EditableCell';
import Authorized from '../Authorization/Authorized';
import {
  getISOLanguagesAction,
  clearErrorsAction,
} from '../../actions/SpecActions/LanguageActions';
import { history } from '../../utils/history';

const { Option } = Select;

const EditableTableWithSelect = ({
  retrieveAll,
  delete: delete1,
  parentSelection,
  dataSource,
  save,
  parentType,
  cardTitle,
  loading,
  location,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  useEffect(() => {
    retrieveAll();
    dispatch(getISOLanguagesAction());
  }, []);

  const { isoLanguageList, error, errorData } = useSelector(store => ({
    isoLanguageList: store.languageReducer.isoLanguageList,
    error: store.languageReducer.error,
    errorData: store.languageReducer.errorData,
  }));

  useEffect(() => {
    if (error && errorData) {
      const link = (
        <a
          role="button"
          tabIndex={0}
          styling="link"
          onKeyDown={() => {
            history.push('/updatePoi/' + errorData[0].id);
          }}
          onClick={() => {
            history.push('/updatePoi/' + errorData[0].id);
          }}
        >
          {errorData[0].title}
        </a>
      );

      const other =
        errorData.length - 1 > 0 ? (
          <span>Other {errorData.length - 1} POIs are pending content.</span>
        ) : (
          ''
        );

      const errorMessage = (
        <span>
          {link} does not have content for the selected language. {other}
        </span>
      );

      dispatch(clearErrorsAction());

      message.error({ content: errorMessage, duration: 5 });
    }
  }, [error]);

  const handleClearSelection = () => {
    setSelectedRowKeys([]);

    if (parentSelection) {
      parentSelection(0);
    }
  };

  const handleMultipleDelete = () => {
    delete1(selectedRowKeys.join(','));
    handleClearSelection();
  };

  const handleAdd = () => {
    form.validateFields().then(values => {
      const data = {
        isoLanguageId: values.language,
        usedInMobileApp: location === 'appCms',
        defaultInMobileApp: false,
      };
      save(data);
    });
  };

  const handleSave = row => {
    
    save(row, row.id);
  };

  const onSelectChange = selected => {
    
    setSelectedRowKeys(selected);
    if (parentSelection) {
      parentSelection(selected[selected.length - 1]);
    }
  };

  const handleAddAppLanguage = row => {
    
    const newData = {
      isoLanguageId: row.isoLanguage.id,
      usedInMobileApp: true,
      defaultInMobileApp: row.defaultInMobileApp,
      directionRightToLeft: row.directionRightToLeft,
    };
    save(newData, row.id);
  };

  const handleRemoveAppLanguage = row => {
    
    const newData = {
      isoLanguageId: row.isoLanguage.id,
      usedInMobileApp: false,
      defaultInMobileApp: row.defaultInMobileApp,
      directionRightToLeft: row.directionRightToLeft,
    };
    save(newData, row.id);
  };

  const handleSetDefault = row => {
    
    const newData = {
      isoLanguageId: row.isoLanguage.id,
      usedInMobileApp: row.usedInMobileApp,
      defaultInMobileApp: true,
      directionRightToLeft: row.directionRightToLeft,
    };
    save(newData, row.id);
  };

  const handleSetDirection = row => {
    
    const newData = {
      isoLanguageId: row.isoLanguage.id,
      usedInMobileApp: row.usedInMobileApp,
      defaultInMobileApp: row.defaultInMobileApp,
      directionRightToLeft: !row.directionRightToLeft,
    };
    save(newData, row.id);
  };

  const handleMenuClick = action => {
    if (action.key === 'appDefault') {
      handleSetDefault(selectedItem);
    }
    if (action.key === 'appLanguage') {
      handleAddAppLanguage(selectedItem);
    }
    if (action.key === 'removeAppLanguage') {
      handleRemoveAppLanguage(selectedItem);
    }
    if (action.key === 'setDirectionRtl') {
      
      handleSetDirection(selectedItem);
      // handleRemoveAppLanguage(selectedItem);
    }
  };

  const baseMenu = (
    <Authorized asset="appcms" operation="write">
      <Menu onClick={handleMenuClick}>
        <Menu.Item key="setDirectionRtl">
          <SwapOutlined />
          Set Direction Right to Left
        </Menu.Item>
      </Menu>
    </Authorized>
  );

  const menuAdd = (
    <Authorized asset="appcms" operation="write">
      <Menu onClick={handleMenuClick}>
        <Menu.Item key="appLanguage">
          <MobileOutlined />
          Add to App languages
        </Menu.Item>
        <Menu.Item key="setDirectionRtl">
          <SwapOutlined />
          Set Direction Right to Left
        </Menu.Item>
      </Menu>
    </Authorized>
  );

  const menuRemove = (
    <Authorized asset="appcms" operation="write">
      <Menu onClick={handleMenuClick}>
        <Menu.Item key="appDefault">
          <HeartOutlined />
          Set as App default language
        </Menu.Item>
        <Menu.Item key="removeAppLanguage">
          <DeleteOutlined />
          Remove from App languages
        </Menu.Item>
        <Menu.Item key="setDirectionRtl">
          <SwapOutlined />
          Set Direction Right to Left
        </Menu.Item>
      </Menu>
    </Authorized>
  );

  const getMenu = record => {
    let menu = '';
    if (!record.usedInMobileApp) {
      menu = (
        <span>
          <Dropdown overlay={menuAdd} trigger={['click']}>
            <a className="ant-dropdown-link" href="#">
              Actions <DownOutlined />
            </a>
          </Dropdown>
        </span>
      );
    } else if (!record.defaultInMobileApp) {
      menu = (
        <span>
          <Dropdown overlay={menuRemove} trigger={['click']}>
            <a className="ant-dropdown-link" href="#">
              Actions <DownOutlined />
            </a>
          </Dropdown>
        </span>
      );
    } else {
      menu = (
        <span>
          <Dropdown overlay={baseMenu} trigger={['click']}>
            <a className="ant-dropdown-link" href="#">
              Actions <DownOutlined />
            </a>
          </Dropdown>
        </span>
      );
    }

    return menu;
  };

  const components = {
    body: {
      row: EditableFormRow,
      cell: EditableCell,
    },
  };
  const columnDefinition = [
    {
      title: 'value',
      dataIndex: ['isoLanguage', 'title'],
      editable: false,
    },
    {
      title: 'code',
      dataIndex: ['isoLanguage', 'code'],
      editable: false,
      width: 40,
    },
    {
      title: 'App Default',
      dataIndex: 'defaultInMobileApp',
      editable: false,
      width: 100,
      render: text => (
        <>
          {text ? (
            <Tag
              style={{
                color: '#5d5de6',
                borderColor: '#5d5de6',
              }}
            >
              App Default
            </Tag>
          ) : null}
        </>
      ),
    },
    {
      title: 'Mobile',
      dataIndex: 'usedInMobileApp',
      editable: false,
      width: 100,
      render: text => (
        <>
          {text ? (
            <Tag
              style={{
                color: '#28cc40',
                borderColor: '#28cc40',
              }}
            >
              App
            </Tag>
          ) : null}
        </>
      ),
    },
    {
      title: 'Direction',
      dataIndex: 'directionRightToLeft',
      editable: false,
      width: 100,
      render: rtl => (
        <>
          {rtl ? (
            <Tag
              style={{
                color: '#28cc40',
                borderColor: '#28cc40',
              }}
            >
              RTL
            </Tag>
          ) : null}
        </>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: record => getMenu(record),
    },
  ];

  const columns = columnDefinition.map(col => {
    if (!col.editable) {
      return {
        ...col,
        onCell: record => ({
          record,
          align: 'center',
        }),
      };
    }
    return {
      ...col,
      onCell: record => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
        parentType,
      }),
    };
  });
  // rowSelection object indicates the need for row selection

  const hasSelected = selectedRowKeys.length > 0;
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const handleModalCancel = () => setModalVisible(false);

  return (
    <div>
      <Card
        title={cardTitle}
        bordered={false}
        loading={loading}
        extra={
          <Row>
            <Authorized asset={parentType} operation="write">
              <Button
                icon={<DeleteOutlined />}
                onClick={handleMultipleDelete}
                disabled={!hasSelected}
                style={{ marginRight: 12 }}
              />
              <Form style={{ width: '200px', marginRight: '20px' }} form={form}>
                <Form.Item name="language" rules={[{ required: true }]}>
                  <Select
                    allowClear
                    showSearch
                    filterOption
                    optionFilterProp="children"
                    // onChange={val => this.handleObjectNumSelection(val)}
                  >
                    {isoLanguageList.map(item => (
                      <Option key={item.id} value={item.id}>
                        {item.title}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Form>

              <Button
                onClick={handleAdd}
                type="primary"
                icon={<PlusOutlined />}
              >
                {/* Add New */}
              </Button>
            </Authorized>
          </Row>
        }
      >
        {/* <Row style={{ paddingBottom: '8px' }}>
            <Search
              placeholder="Search"
              onSearch={value => handleSearch(value)}
              defaultValue={filterValue}
              allowClear
            />
          </Row> */}
        <Row justify="center" align="middle">
          <Col>
            <Table
              components={components}
              rowClassName={() => 'editable-row'}
              bordered={false}
              dataSource={dataSource}
              columns={columns}
              pagination={false}
              rowKey={record => record.id}
              size="small"
              rowSelection={rowSelection}
              showHeader={false}
              scroll={{ y: 550 }}
              onRow={record => {
                return {
                  onClick: () => setSelectedItem(record),
                };
              }}
            />
          </Col>
        </Row>
      </Card>
      <Modal
        visible={modalVisible}
        footer={null}
        onCancel={handleModalCancel}
        closable={false}
        destroyOnClose
      />
    </div>
  );
};

EditableTableWithSelect.propTypes = {
  dataSource: PropTypes.array,
  retrieveAll: PropTypes.func,
  save: PropTypes.func,
  delete: PropTypes.func,
  cardTitle: PropTypes.string,
  loading: PropTypes.bool,
  parentSelection: PropTypes.func,
  parentType: PropTypes.string,
  location: PropTypes.string,
};

export default EditableTableWithSelect;
